import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  Alert,
  Spin,
  Select,
  Upload,
  message,
} from 'antd';
import CustomDateTimePicker from '../components/CustomDateTimePicker';
import { UploadOutlined } from '@ant-design/icons';
import { uploadImage } from '../services/uploadService';
import { EditOutlined, DeleteOutlined, PlusOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import {
  getEvents,
  createEvent,
  updateEvent,
  deleteEvent,
  allEvents,
} from '../services/eventsService';
import moment from 'moment';

function RelatedEventsAdmin() {
  const { eventName } = useParams();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [form] = Form.useForm();
  const { Title } = Typography;

  useEffect(() => {
    fetchEvents();
  }, [eventName]);

  const fetchEvents = async () => {
    try {
      const allE = await allEvents();
      const relatedEvents = allE.filter(event => event.name === decodeURIComponent(eventName));
      setEvents(relatedEvents);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (event = null) => {
    if (event) {
      setEditingEvent(event);
      form.setFieldsValue({
        name: event.name,
        description: event.description || '',
        ticket_url: event.ticket_url || '',
        image_url: event.image_url || '',
        start_date: moment(event.start_date),
        end_date: moment(event.end_date),
        status: event.status,
        city_id: event.city_id,
        location: event.location,
      });
    } else {
      setEditingEvent(null);
      form.resetFields();
      // Get the first event's details to copy
      const templateEvent = events.find(e => e.name === decodeURIComponent(eventName));
      form.setFieldsValue({
        name: decodeURIComponent(eventName),
        description: templateEvent?.description || '',
        image_url: templateEvent?.image_url || '',
        ticket_url: templateEvent?.ticket_url || '',
        city_id: templateEvent?.city_id,
        location: templateEvent?.location,
        status: 'upcoming',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseModal = () => {
    setOpenDialog(false);
    setEditingEvent(null);
    form.resetFields();
  };

  const handleSubmit = async values => {
    const eventData = {
      ...values,
      start_date: values.start_date.toISOString(),
      end_date: values.end_date.toISOString(),
    };

    setLoading(true);
    try {
      if (editingEvent) {
        await updateEvent(editingEvent.id, eventData);
      } else {
        await createEvent(eventData);
      }
      fetchEvents();
      handleCloseModal();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async id => {
    Modal.confirm({
      title: 'Are you sure you want to delete this event occurrence?',
      onOk: async () => {
        setLoading(true);
        try {
          await deleteEvent(id);
          fetchEvents();
        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const columns = [
    {
      title: 'Start Date',
      dataIndex: 'start_date',
      key: 'start_date',
      render: date => new Date(date).toLocaleDateString(),
      sorter: (a, b) => new Date(a.start_date) - new Date(b.start_date),
    },
    {
      title: 'End Date',
      dataIndex: 'end_date',
      key: 'end_date',
      render: date => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button type="text" icon={<EditOutlined />} onClick={() => handleOpenModal(record)} />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          />
        </Space>
      ),
    },
  ];

  if (loading)
    return (
      <div style={{ textAlign: 'center', padding: '24px' }}>
        <Spin size="large" />
      </div>
    );
  if (error) return <Alert message={error} type="error" />;

  return (
    <div style={{ padding: '24px' }}>
      <div
        style={{
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/events')}>
            Back to Events
          </Button>
          <Title level={2} style={{ margin: 0 }}>
            {decodeURIComponent(eventName)}
          </Title>
        </Space>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenModal()}>
          Add Occurrence
        </Button>
      </div>

      <Table columns={columns} dataSource={events} rowKey="id" />

      <Modal
        title={editingEvent ? 'Edit Event' : 'Add Event'}
        open={openDialog}
        onCancel={handleCloseModal}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input disabled />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item
            hidden
            name="city_id"
            label="City"
            rules={[{ required: true, message: 'Please select a city!' }]}
          >
            <Select>
              {cities &&
                cities.map(city => (
                  <Select.Option key={city.id} value={city.id}>
                    {city.name}, {city.state}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
          <Form.Item name="location" label="Location">
            <Input />
          </Form.Item>

          <Form.Item
            name="start_date"
            label="Start Date & Time"
            rules={[
              {
                required: true,
                message: 'Please select the start date and time!',
              },
            ]}
          >
            <CustomDateTimePicker />
          </Form.Item>
          <Form.Item
            name="end_date"
            label="End Date & Time"
            rules={[
              {
                required: true,
                message: 'Please select the end date and time!',
              },
            ]}
          >
            <CustomDateTimePicker />
          </Form.Item>
          <Form.Item
            hidden
            name="status"
            label="Status"
            rules={[{ required: true, message: 'Please select a status!' }]}
          >
            <Select>
              <Select.Option value="upcoming">Upcoming</Select.Option>
              <Select.Option value="ongoing">Ongoing</Select.Option>
              <Select.Option value="completed">Completed</Select.Option>
              <Select.Option value="cancelled">Cancelled</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="ticket_url" label="Ticket URL">
            <Input />
          </Form.Item>
          <Form.Item name="image_url" label="Image">
            <Upload
              maxCount={1}
              accept="image/*"
              beforeUpload={async file => {
                try {
                  const url = await uploadImage(file);
                  form.setFieldsValue({ image_url: url });
                  message.success('Image uploaded successfully');
                } catch (err) {
                  message.error('Failed to upload image');
                }
                return false;
              }}
            >
              <Button icon={<UploadOutlined />}>Upload Image</Button>
            </Upload>
            {form.getFieldValue('image_url') && (
              <img
                src={form.getFieldValue('image_url')}
                alt="Preview"
                style={{
                  marginTop: 8,
                  maxWidth: '100%',
                  maxHeight: 200,
                  objectFit: 'contain',
                }}
              />
            )}
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleCloseModal}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingEvent ? 'Update' : 'Create'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default RelatedEventsAdmin;
